import React, { useState, useEffect } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { logout, changePassword, setLocale } from "../../store/actions";
import { ActionButton, TextInput } from "colbi_web_ui/lib/components/inputs";
import { SovosIcon } from "mosaic-react";
import { CheckCircleOutline, WarningAmber } from "mosaic-react-icons";

import styles from "./ChangePassword.module.sass";
import I18n from "../../I18n";
import { decodeUserSession } from "../../utils/utils";

const ChangePassword = (props) => {
    const { user, resetPasswordError, setLocale, match, changePassword } = props;
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [disabled, setDisabled] = useState(true);
    const [status, setStatus] = useState({
        lowUpperCase: false,
        oneNumber: false,
        eightCharacter: false,
    });
    const location = useLocation();
    const [hash, setHash] = useState(null);
    const [errorPasswordHideClass, setErrorPasswordHideClass] = useState(true);
    const [errorConfirmPasswordHideClass, setErrorConfirmPasswordHideClass] = useState(true);

    useEffect(() => {
        setHash(location.pathname.split("/")[2]);
    }, [location.pathname]);

    const checkStrength = (password) => {
        let strength = 0;

        if (password.match(/([a-z].*[A-Z])|([A-Z].*[a-z])/)) {
            strength += 2;

            setStatus((prevState) => ({
                ...prevState,
                lowUpperCase: true,
            }));

            setErrorPasswordHideClass(true);
        } else {
            setStatus((prevState) => ({
                ...prevState,
                lowUpperCase: false,
            }));

            setErrorPasswordHideClass(false);
        }

        if (password.match(/([0-9])/)) {
            strength += 1;

            setStatus((prevState) => ({
                ...prevState,
                oneNumber: true,
            }));

            setErrorPasswordHideClass(true);
        } else {
            setStatus((prevState) => ({
                ...prevState,
                oneNumber: false,
            }));

            setErrorPasswordHideClass(false);
        }

        if (password.length > 9) {
            strength += 1;

            setStatus((prevState) => ({
                ...prevState,
                eightCharacter: true,
            }));

            setErrorPasswordHideClass(true);
        } else {
            setStatus((prevState) => ({
                ...prevState,
                eightCharacter: false,
            }));

            setErrorPasswordHideClass(false);
        }

        if (password.match(/([!,%,&,@,#,$,^,*,?,_,~])/)) {
            strength += 1;
        }

        // Password Strength:
        switch (strength) {
            case 3: {
                return "Weak";
            }
            case 4: {
                return "Medium";
            }
            case 5: {
                return "Strong";
            }
            default: {
                return "Very Weak";
            }
        }
    };

    const handleSubmit = (user, e) => {
        e.preventDefault();
        const resultChangePassword = changePassword(newPassword, confirmPassword, hash);
        if (resultChangePassword) {
            return <Redirect to={`/${locale}/login`} push />;
        }
    };

    const handlePassword = (e) => {
        const { value } = e.target;

        setNewPassword(value);

        if (checkStrength(value) !== "Medium" || checkStrength(value) !== "Strong") {
            setDisabled(true);
        }

        if (confirmPassword === value) {
            setErrorConfirmPasswordHideClass(true);

            if (checkStrength(value) === "Strong" || checkStrength(value) === "Medium") {
                setDisabled(false);
            }
        }
    };

    const handleConfirmPassword = (e) => {
        const confirmPassword = e.target.value;

        setConfirmPassword(confirmPassword);

        if (newPassword === confirmPassword) {
            setErrorConfirmPasswordHideClass(true);

            if (
                checkStrength(newPassword) === "Medium" ||
                checkStrength(newPassword) === "Strong"
            ) {
                setDisabled(false);
            }
        } else {
            setDisabled(true);
            setErrorConfirmPasswordHideClass(false);
        }
    };

    const { lowUpperCase, oneNumber, eightCharacter } = status;

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);
    setLocale(locale);
    const userInformation = decodeUserSession(user);

    function presentIconAccordingToValidation(validation) {
        return validation ? (
            <SovosIcon>
                <CheckCircleOutline />
            </SovosIcon>
        ) : (
            <SovosIcon>
                <WarningAmber />
            </SovosIcon>
        );
    }

    return (
        <div className={styles["root"]}>
            <div className={styles["main"]}>
                <div className={styles["login-container"]}>
                    <div className={styles["details"]}></div>
                    <h1 className={styles["title"]}>{i18n`Reset Password`}</h1>
                    {resetPasswordError && (
                        <div className={styles["error"]}>{resetPasswordError}</div>
                    )}
                    <form
                        className={styles["form"]}
                        onSubmit={(e) => {
                            handleSubmit(userInformation, e);
                        }}
                    >
                        <label>
                            <TextInput
                                type="password"
                                autoComplete="off"
                                name={i18n`New password`}
                                placeholder=""
                                required
                                value={newPassword}
                                onChange={(e) => {
                                    handlePassword(e);
                                }}
                            />
                            <div>
                                <small
                                    className={
                                        errorPasswordHideClass
                                            ? styles["hide"]
                                            : styles["msg-input-error"]
                                    }
                                >
                                    {i18n`Enter a strong password. Check the requirements.`}
                                </small>
                            </div>
                        </label>
                        <label>
                            <TextInput
                                type="password"
                                autoComplete="off"
                                name={i18n`Confirm password`}
                                placeholder=""
                                required
                                value={confirmPassword}
                                onChange={(e) => {
                                    handleConfirmPassword(e);
                                }}
                            />
                            <div>
                                <small
                                    className={
                                        errorConfirmPasswordHideClass
                                            ? styles["hide"]
                                            : styles["msg-input-error"]
                                    }
                                >
                                    {i18n`Passwords don't match`}
                                </small>
                            </div>
                        </label>
                        <div className={styles["reset-actions"]}>
                            <ActionButton
                                primary
                                className={styles["action-button"]}
                                type="submit"
                                disabled={disabled}
                            >{i18n`Reset`}</ActionButton>
                        </div>
                    </form>
                </div>
            </div>
            <div className={styles["aside"]}>
                <h2 className={styles["title"]}>{i18n`Instructions`}</h2>
                <p className={styles["subtitle"]}>{i18n`Minimum Requirements`}</p>
                <div className={styles["wrap-strength-password"]}>
                    <ul className={styles["list-unstyled"]}>
                        <li className={`${lowUpperCase ? styles["text-success"] : "text"} `}>
                            {presentIconAccordingToValidation(lowUpperCase)}
                            <span>{i18n`1 lowercase and 1 uppercase`}</span>
                        </li>
                        <li className={`${oneNumber ? styles["text-success"] : "text"}`}>
                            {presentIconAccordingToValidation(oneNumber)}
                            <span>{i18n`1 number (0-9)`}</span>
                        </li>
                        <li className={`${eightCharacter ? styles["text-success"] : "text"}`}>
                            {presentIconAccordingToValidation(eightCharacter)}
                            <span>{i18n`At least 10 Character`}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default connect(
    ({ user, resetPasswordError, fetch }) => ({
        user,
        resetPasswordError,
        fetch,
    }),
    {
        logout,
        changePassword,
        setLocale,
    },
)(ChangePassword);
